/**
 * ATL-1445 stage 3, P4 — the device's memory of each organization's write master.
 *
 * Why a flag is remembered at all, and in which direction a memory may be trusted, is in
 * `writeMode.ts` ("WHY THE WRITE FLAG IS REMEMBERED WHEN THE READ FLAG IS NOT"). This file only
 * stores and loads it:
 *
 *   - one AsyncStorage key PER ORGANIZATION (`WRITES_MASTER_MEMORY_PREFIX` + the organization's
 *     Firestore id), so signing into another organization can never inherit this one's answer — the
 *     first of the two reasons `orgReadCutover.ts` gives for not caching the read flag;
 *   - a small versioned JSON value, `{ v: 1, writesMaster, observedAt }`. Anything else read back is
 *     `unreadable`, never a guess and never silently deleted (a human may want to see it);
 *   - every call resolves, never rejects: this runs on the startup path, and a storage failure must
 *     not take the app down over a flag whose safe handling is already decided in `writeMode.ts`.
 *
 * AsyncStorage is enough here, unlike for outbox v2 (stage-3 §2): one short string per organization
 * the device has signed into, far below Android's 6 MB database cap and web's localStorage quota.
 */
import AsyncStorage from '@react-native-async-storage/async-storage'
import type { LastKnownWritesMaster, WritesMaster } from './writeMode'
import { writesMasterOfField } from './writeMode'

export const WRITES_MASTER_MEMORY_PREFIX = 'atlas.write-cutover.writes-master.v1:'

/** The subset of AsyncStorage this module uses (injectable for tests). */
export interface WritesMasterStorage {
  getItem(key: string): Promise<string | null>
  setItem(key: string, value: string): Promise<void>
}

const keyFor = (organizationId: string): string => `${WRITES_MASTER_MEMORY_PREFIX}${organizationId}`

/** Parse a stored value. Anything but the exact v1 shape with a readable master is `unreadable`. */
export const parseRememberedWritesMaster = (stored: string | null): LastKnownWritesMaster => {
  if (stored === null) return { status: 'none' }
  let parsed: unknown
  try {
    parsed = JSON.parse(stored)
  } catch {
    return { status: 'unreadable' }
  }
  if (!parsed || typeof parsed !== 'object') return { status: 'unreadable' }
  const record = parsed as Record<string, unknown>
  // `writesMasterOfField` reads an ABSENT field as 'firestore' — right for the organization document,
  // wrong for our own record, which always writes it. So absence is checked first.
  if (record.v !== 1 || record.writesMaster === undefined || record.writesMaster === null) return { status: 'unreadable' }
  const value = writesMasterOfField(record.writesMaster)
  if (!value || typeof record.observedAt !== 'string') return { status: 'unreadable' }
  return { status: 'known', value, observedAt: record.observedAt }
}

export interface WritesMasterMemory {
  /** What this device last knew for the organization. Never rejects. */
  recall(organizationId: string): Promise<LastKnownWritesMaster>
  /** Remember a value. Resolves to whether it was stored; never rejects. */
  remember(organizationId: string, writesMaster: WritesMaster, observedAt: Date): Promise<boolean>
}

/**
 * How long `recall` waits for storage before answering `unreadable`. `AppStack.getInitialData` awaits
 * the recall (started in parallel with the organization-document read), so a storage call that never
 * settles must not be able to hold app start hostage; `unreadable` is already a handled answer.
 */
export const WRITES_MASTER_RECALL_TIMEOUT_MS = 2000

const RECALL_TIMED_OUT: unique symbol = Symbol('writes-master-recall-timed-out')

export const createWritesMasterMemory = (
  storage: WritesMasterStorage,
  logger: Pick<Console, 'warn'> = console,
  recallTimeoutMs: number = WRITES_MASTER_RECALL_TIMEOUT_MS,
): WritesMasterMemory => ({
  recall: async (organizationId) => {
    if (!organizationId) return { status: 'none' }
    let timer: ReturnType<typeof setTimeout> | undefined
    const timedOut = new Promise<typeof RECALL_TIMED_OUT>((resolve) => {
      timer = setTimeout(() => resolve(RECALL_TIMED_OUT), recallTimeoutMs)
    })
    try {
      const stored = await Promise.race([storage.getItem(keyFor(organizationId)), timedOut])
      if (stored === RECALL_TIMED_OUT) {
        logger.warn('[write-mode] the remembered write master did not load in time', { organizationId, recallTimeoutMs })
        return { status: 'unreadable' }
      }
      return parseRememberedWritesMaster(stored)
    } catch (error) {
      logger.warn('[write-mode] could not read the remembered write master', { organizationId, error })
      return { status: 'unreadable' }
    } finally {
      if (timer !== undefined) clearTimeout(timer)
    }
  },
  remember: async (organizationId, writesMaster, observedAt) => {
    if (!organizationId) return false
    try {
      await storage.setItem(
        keyFor(organizationId),
        JSON.stringify({ v: 1, writesMaster, observedAt: observedAt.toISOString() }),
      )
      return true
    } catch (error) {
      logger.warn('[write-mode] could not remember the write master', { organizationId, writesMaster, error })
      return false
    }
  },
})

/** The app's memory, over AsyncStorage (localStorage on web). */
export const writesMasterMemory: WritesMasterMemory = createWritesMasterMemory(AsyncStorage)
